import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BlogCard from '@/components/BlogCard';
import UniversityCard from '@/components/UniversityCard';
import CourseCard from '@/components/CourseCard';
import { Search as SearchIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState({ blogs: [], universities: [], courses: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      if (!query) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        setResults(response.data);
      } catch (error) {
        console.error('Error searching:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query]);

  const totalResults = results.blogs.length + results.universities.length + results.courses.length;

  return (
    <div className="py-12 bg-white">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-foreground mb-4">
            Search Results
          </h1>
          {query && (
            <p className="text-lg text-muted-foreground">
              Found {totalResults} results for "{query}"
            </p>
          )}
        </div>

        {loading ? (
          <div className="space-y-8">
            <div className="h-32 bg-secondary animate-pulse rounded-sm" />
            <div className="h-32 bg-secondary animate-pulse rounded-sm" />
          </div>
        ) : !query ? (
          <div className="text-center py-12">
            <SearchIcon size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Enter a search query to find content</p>
          </div>
        ) : totalResults === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No results found for "{query}"</p>
          </div>
        ) : (
          <div className="space-y-12">
            {results.blogs.length > 0 && (
              <section data-testid="blog-results">
                <h2 className="text-2xl font-playfair font-bold text-foreground mb-6">Articles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {results.blogs.map((post) => (
                    <BlogCard key={post.post_id} post={post} />
                  ))}
                </div>
              </section>
            )}

            {results.universities.length > 0 && (
              <section data-testid="university-results">
                <h2 className="text-2xl font-playfair font-bold text-foreground mb-6">Universities</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {results.universities.map((uni) => (
                    <UniversityCard key={uni.university_id} university={uni} />
                  ))}
                </div>
              </section>
            )}

            {results.courses.length > 0 && (
              <section data-testid="course-results">
                <h2 className="text-2xl font-playfair font-bold text-foreground mb-6">Courses</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {results.courses.map((course) => (
                    <CourseCard key={course.course_id} course={course} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}